<template>
  <BasePanel :headline="$t('lang_LABEL_CONTRACTS_PANEL_CAPTION_COSTS') + ' (' + $t('lang_GENERIC_CAPTION_BETA_DO_NOT_USE') + ')'" :alvqa="alvqa">
    <template v-slot:panelContent>
      <v-container fluid class="pl-0">
        <v-row>
          <v-col>
            <v-data-table
              :headers="headers"
              :items="listItems"
              hide-default-footer
              :items-per-page="300"
              :header-props="{ sortIcon: '' }"
              :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
              class="noMinWidth"
            >
              <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
              </template>

              <template v-slot:[`item.costGroup`]="{ item }">
                <div class="ellipsis">
                  <BaseTooltip 
                    :simpleText="getCostGroup(item.costGroup)"
                    :alvqa="alvqa + '-list-row-text-cost-group'"
                  />
                </div>
              </template>
              <template v-slot:[`item.date`]="{ item }">
                <div 
                  class="text-right"
                  :alvqa="alvqa + '-list-row-text-date'"
                >
                  {{ item.date !== null && item.date !== "0000-00-00" ? $d(new Date(item.date), 'alvDateFormat') : '' }}
                </div>
              </template>
              <template v-slot:[`item.recipient`]="{ item }">
                <div class="ellipsis">
                  <BaseTooltip 
                    :simpleText="item.recipient !== null ? item.recipient.gemaName : ''"
                    :alvqa="alvqa + '-list-row-text-recipient'"
                  />
                </div>
              </template>
              <template v-slot:[`item.description`]="{ item }">
                <div class="ellipsis">
                  <BaseTooltip 
                    :simpleText="item.description"
                    :alvqa="alvqa + '-list-row-text-description'"
                  />
                </div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div 
                  class="text-right"
                  :alvqa="alvqa + '-list-row-text-amount'"
                >
                  {{ $alvPlugin.formatMoney(item.amount) }}
                </div>
              </template>
              <template v-slot:[`item.documentNumber`]="{ item }">
                <div class="ellipsis">
                  <BaseTooltip 
                    :simpleText="item.documentNumber"
                    :alvqa="alvqa + '-list-row-text-document-number'"
                  />
                </div>
              </template>
              <template v-slot:[`item.country`]="{ item }">
                <div class="text-no-wrap">
                  <BaseTooltip>
                    <template v-slot:content>
                      <div :alvqa="alvqa + '-list-row-text-country'">{{ $alvPlugin.getTranslatedCountryName(item.country, true) }}</div>
                    </template>
                    <template v-slot:text>{{ item.country }}</template>
                  </BaseTooltip>
                </div>
              </template>
              <template v-slot:[`item.paidOn`]="{ item }">
                <div 
                  class="text-right text-no-wrap"
                  :alvqa="alvqa + '-list-row-text-paid-on'"
                >
                  {{ item.paidOn !== null ? $d(new Date(item.paidOn), 'alvDateFormat') : '' }}
                </div>
              </template>
              <template v-slot:[`item.accountingPeriod`]="{ item }">
                <div 
                  class="text-right text-no-wrap"
                  :alvqa="alvqa + '-list-row-text-accounting-period'"
                >
                  {{ $alvPlugin.displayPeriod(item.accountingPeriod) }}
                </div>
              </template>
              <template v-slot:[`item.split`]="{ item }">
                <div 
                  class="text-right text-no-wrap"
                  :alvqa="alvqa + '-list-row-text-split'"
                >
                  {{ $alvPlugin.getNumberLocalization(item.split, 'number') }}
                </div>
              </template>
              <template v-slot:[`item.assignToWork`]="{ item }">
                <div class="ellipsis">
                  <BaseTooltip 
                    :simpleText="getAssignToWork(item.assignToWork)"
                    :alvqa="alvqa + '-list-row-text-assign-to-work'"
                  />
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </BasePanel>
</template>

<script>
import { DropdownPublishingContractsCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'
import BasePanel from "@/components/_base/BasePanel";
import BaseTooltip from "@/components/_base/BaseTooltip";

export default {
  name: "PublishingContractsCostsReadOnlyPanel",
  components: {
    BasePanel,
    BaseTooltip,
  },
  props: {
    itemId: {
      type: Number,
    },
  },
  data() {
    return {
      alvqa: "publishing-contracts-costs-list-row-" + this.itemId + "-read-only",
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("lang_LABEL_CONTRACTS_CAPTION_COST_GROUP"),
          align: "left",
          value: "costGroup",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-cost-group",
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_DATE"),
          align: "right",
          value: "date",
          width: "110px",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-date",
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_RECIPIENT"),
          align: "left",
          value: "recipient",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-recipient",
        },
        {
          text: this.$t("lang_LABEL_CONTRACTS_CAPTION_DESCRIPTION"),
          align: "left",
          value: "description",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-description",
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_AMOUNT") + " " + this.$store.getters.getSelectedCurrency,
          align: "right",
          value: "amount",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-amount",
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_DOCUMENT_NUMBER_SHORT"),
          align: "left",
          value: "documentNumber",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-document-number",
        },
        {
          text: this.$t("lang_GENERAL_CAPTION_COUNTRY"),
          align: "left",
          value: "country",
          width: "110px",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-country",
        },
        {
          text: this.$t("lang_LABEL_CONTRACTS_CAPTION_PAID_ON"),
          align: "right",
          value: "paidOn",
          width: "110px",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-paid-on",
        },
        {
          text: this.$t("lang_GENERIC_CAPTION_PERIOD"),
          align: "right",
          value: "accountingPeriod",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-accounting-period",
        },
        {
          text: this.$t("lang_GENERAL_CAPTION_SPLIT") + " %",
          align: "right",
          value: "split",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-split",
        },
        {
          text: this.$t("lang_PUBLISHING_CONTRACTS_CAPTION_COST_WORK_RELATION"),
          align: "left",
          value: "assignToWork",
          class: "text-no-wrap",
          sortable: false,
          alvqa: this.alvqa + "-header-assign-to-work",
        },
      ];
    },
    listItems() {
      return this.$store.getters.getPublishingContractsCosts;
    },
  },
  methods: {
    getCostGroup(costGroupValue) {
      let types = DropdownPublishingContractsCostGroupTypes;
      if (types !== undefined) {
        let match = types.find(type => type.value === costGroupValue);
        if (match !== undefined) return this.$t(match.text);
        else return "";
      } else return "";
    },
    getAssignToWork(assignToWork) {
      if (assignToWork !== null) {
        return assignToWork.title + " (" + assignToWork.alvId + ")";
      } else return "";
    },
  },
};
</script>
